import {Reducer, updateFactory} from '@modifi/redux'
import {aReplace} from '@modifi/router'
import {reject} from 'ramda'
import {TransferMethod} from '@modifi/enums'
import {
  getAuthenticatedThemisClient,
  DirectDebitBankAccountView,
} from '@modifi/onboarding-api-clients'
import {aUserFeedBackSubmitFailed, aUserFeedBackSubmitSuccessful} from '@modifi/plugin-onboarding'

import {
  aEnsureDirectDebitBankAccounts,
  aUpdateDirectDebitBankAccount,
  selectDirectDebitBankAccountByExternalId,
  aFetchDirectDebitBankAccounts,
} from '../../../data/direct-debit-account'
import {aFetchUser} from '../../../data/user'
import {
  selectCompanyAddress,
  selectCurrentCompaniesCountryCodeLowerCased,
} from '../../../data/company'
import {
  mapDirectDebitAccountToFormData,
  pickAllDirectDebitAccountProperties,
  mapDirectDebitAccountToRequest,
} from './utils'
import {ROUTE_COMPANY_PROFILE_DIRECT_DEBIT_ACCOUNT_DETAILS} from '../../../routes/constants'
import {aFetchInputFields} from '../../../data/input-fields'
import {SECTION_DIRECT_DEBIT_ACCOUNT_DETAILS} from '../../../constants/sections'
import {AppThunkAction} from '../../../types/redux'

export const aInitializePage =
  (): AppThunkAction =>
  async (dispatch): Promise<void> => {
    dispatch(aEnsureDirectDebitBankAccounts())
  }

export const selectInitialFormData = (
  state: Record<string, any>,
  externalBankAccountId?: string
): {initialValues: Record<string, any>; defaultValues: Record<string, any>} => {
  let initialValues = {}
  let defaultValues = {}
  if (externalBankAccountId) {
    const directDebitAccount = selectDirectDebitBankAccountByExternalId(
      state,
      externalBankAccountId
    )
    if (directDebitAccount) initialValues = mapDirectDebitAccountToFormData(directDebitAccount)
  } else {
    const {
      city,
      companyName,
      countryCode,
      state: companyState,
      streetName,
      streetNumber,
      zipCode,
    } = selectCompanyAddress(state) ?? {}

    defaultValues = mapDirectDebitAccountToFormData({
      accountHolder: companyName,
      state: companyState,
      city,
      countryCode,
      streetName,
      streetNumber,
      zipCode,
    })
  }
  return {
    initialValues,
    defaultValues,
  }
}

const apiClient = getAuthenticatedThemisClient()

const {actions, selectors, reducer} = updateFactory({
  actionNamePrefix: 'pages/direct-debit-bank-account/submit',
  rootSelector: state => state?.pages?.directDebitBankAccount,
  submitRequest:
    (a, externalBankAccountId: string | undefined, changes: Record<string, any>) =>
    async (dispatch, getState): Promise<void> => {
      const currentCompaniesCountryCode =
        selectCurrentCompaniesCountryCodeLowerCased(getState()) ?? undefined
      let result = {} as DirectDebitBankAccountView
      const directDebitRequestProperties = mapDirectDebitAccountToRequest(
        pickAllDirectDebitAccountProperties(changes)
      )
      const body = {
        ...directDebitRequestProperties,
      }

      dispatch(a.pendingAction())
      try {
        if (externalBankAccountId) {
          // update existing bank account
          result = await apiClient.DirectDebitBankAccounts.update({
            externalBankAccountId,
            body,
          }).then(res => res.data())
        } else {
          // create new bank account
          body.transferMethod = TransferMethod.SEPA // only SEPA is valid for direct debit bank accounts
          result = await apiClient.DirectDebitBankAccounts.create({
            body: reject(value => value === '', body), // @todo find out why this is necessary
          }).then(res => res.data())
        }
      } catch (e: any) {
        dispatch(a.errorAction(e))
        dispatch(
          aUserFeedBackSubmitFailed(
            SECTION_DIRECT_DEBIT_ACCOUNT_DETAILS,
            currentCompaniesCountryCode
          )
        )
        return
      }

      dispatch(aUserFeedBackSubmitSuccessful(SECTION_DIRECT_DEBIT_ACCOUNT_DETAILS))
      dispatch(aUpdateDirectDebitBankAccount(result))

      await Promise.all([
        dispatch(aFetchDirectDebitBankAccounts()),
        dispatch(aFetchInputFields()),
        dispatch(aFetchUser()),
      ]).catch(() => {
        /* ignore error */
      })

      dispatch(aReplace(ROUTE_COMPANY_PROFILE_DIRECT_DEBIT_ACCOUNT_DETAILS))

      dispatch(a.successAction())
    },
})

export default reducer as Reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateErrorMessage: selectSaveErrorMessage,

  selectUpdateErrorFieldErrors: selectSaveErrorFieldErrors,
} = selectors

export const {aSubmit: aSavePage, aReset: aClearError} = actions
